import React, { useState, useEffect, useMemo } from 'react';
import { fmtDate, fmtDateTime } from '@shared/formatDate';
import { useQuery } from '@tanstack/react-query';
import {
  Container,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Paper,
  Drawer,
  IconButton,
  Chip,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Stack,
  Collapse,
} from '@mui/material';
import { 
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  People as PeopleIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  FamilyRestroom as FamilyIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import ProfileView from '../components/profile/ProfileView';
import ProfileAliasNavigator from '../components/profile/ProfileAliasNavigator';
import EnhancedProfileEditor from '../components/profile/EnhancedProfileEditor';
import AliasManager from '../components/profile/AliasManager';
import ChangePassword from '../components/profile/ChangePassword';
import AccountSettings from '../components/profile/AccountSettings';
import MinorAccessManager from '../components/profile/MinorAccessManager';
import AdditionalMembersManager from '../components/profile/AdditionalMembersManager';
import { useAuth } from '../hooks/useAuth';
import { getVisibleTabKeys } from '../services/configService';
import {
  sanitizeMinorAllowedTabsForRuntime,
} from '../utils/minorTabPolicy';
import socialService, { ProfileDto, ProfileAliasDto } from '../services/socialService';
import jointAccountService, { JointAccountAlias } from '../services/jointAccountService';
import { isStoreDistribution, isNativeShell } from '../utils/distribution';
import ResponsiveScrollTabs from '../components/layout/ResponsiveScrollTabs';
import userAccountService, { 
  AliasAccountDetails,
  ReferralLinksResponse, 
  ReferralSchedule, 
  ReferredUser 
} from '../services/userAccountService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const MyProfile: React.FC = () => {
  const storeMode = isStoreDistribution();
  const { user, isBusinessAccount, hasAdvancedMailbox, isJointContext, jointAccountName, isMinor, aliasId, jointAccountId, minorAllowedTabs } = useAuth();
  const [resolvedIsBusinessAccount, setResolvedIsBusinessAccount] = useState(isBusinessAccount);
  const { data: visibleTabKeys = [] } = useQuery({
    queryKey: ['visibleTabKeys'],
    queryFn: getVisibleTabKeys,
    staleTime: 5 * 60 * 1000,
    enabled: true,
  });
  const [currentTab, setCurrentTab] = useState(0);

  const [aliasDetails, setAliasDetails] = useState<AliasAccountDetails | null>(null);
  const [aliasDisplayName, setAliasDisplayName] = useState('');
  const [savingDisplayName, setSavingDisplayName] = useState(false);
  const [aliasDisplayNameSuccess, setAliasDisplayNameSuccess] = useState(false);

  const effectiveMinorAllowedTabs = useMemo(
    () =>
      isMinor
        ? sanitizeMinorAllowedTabsForRuntime(minorAllowedTabs, visibleTabKeys, hasAdvancedMailbox)
        : [],
    [isMinor, minorAllowedTabs, visibleTabKeys, hasAdvancedMailbox],
  );

  const profileTabKeys = useMemo(() => {
    if (isMinor) {
      return ['profile/your-profiles', 'profile/account-settings'];
    }
    return [
      'profile/your-profiles',
      'profile/account-settings',
      resolvedIsBusinessAccount ? 'profile/additional-members' : 'profile/minor-access',
    ];
  }, [isMinor, resolvedIsBusinessAccount]);

  const visibleTabIndices = useMemo(() => {
    if (isMinor) {
      return profileTabKeys
        .map((key, index) => (effectiveMinorAllowedTabs.includes(key) ? index : -1))
        .filter((index) => index >= 0);
    }
    const visible = (key: string) => hasAdvancedMailbox || (visibleTabKeys.length === 0 || visibleTabKeys.includes(key));
    return profileTabKeys.map((key, i) => visible(key) ? i : -1).filter((i) => i >= 0);
  }, [isMinor, effectiveMinorAllowedTabs, hasAdvancedMailbox, visibleTabKeys, profileTabKeys]);

  const realTabIndex = visibleTabIndices[currentTab] ?? visibleTabIndices[0] ?? 0;
  const tabLabels = isMinor
    ? ['Your Profiles', 'Account Settings']
    : ['Your Profiles', 'Account Settings', resolvedIsBusinessAccount ? 'Additional Members' : 'Minor Access'];

  const profileTabShortLabels = isMinor
    ? ['Profiles', 'Settings']
    : ['Profiles', 'Settings', resolvedIsBusinessAccount ? 'Members' : 'Minors'];

  const profileTabs = useMemo(
    () =>
      visibleTabIndices.map((realIndex, displayIndex) => ({
        value: String(displayIndex),
        label: tabLabels[realIndex] ?? `Tab ${displayIndex + 1}`,
        selectLabel:
          realIndex === 0
            ? 'Profiles'
            : realIndex === 1
              ? 'Account Settings'
              : resolvedIsBusinessAccount
                ? 'Members'
                : 'Minors',
        shortLabel: profileTabShortLabels[realIndex] ?? tabLabels[realIndex] ?? `Tab ${displayIndex + 1}`,
      })),
    [visibleTabIndices, tabLabels, profileTabShortLabels, resolvedIsBusinessAccount],
  );

  const nativeShell = isNativeShell();

  useEffect(() => {
    if (visibleTabIndices.length > 0 && currentTab >= visibleTabIndices.length) setCurrentTab(0);
  }, [visibleTabIndices.length, currentTab]);

  useEffect(() => {
    if (visibleTabIndices.length === 0) return;

    const params = new URLSearchParams(window.location.search);
    const tab = (params.get('tab') || '').toLowerCase();
    const targetRealTab =
      tab === 'account-settings' || tab === 'settings' ? 1 :
      tab === 'your-profiles' || tab === 'profiles' ? 0 :
      tab === 'additional-members' || tab === 'minor-access' ? 2 :
      -1;

    if (targetRealTab < 0) return;
    const displayIndex = visibleTabIndices.indexOf(targetRealTab);
    if (displayIndex >= 0) {
      setCurrentTab(displayIndex);
    }
  }, [visibleTabIndices]);

  useEffect(() => {
    setResolvedIsBusinessAccount(isBusinessAccount);
  }, [isBusinessAccount]);

  useEffect(() => {
    const loadResolvedBusinessFlag = async () => {
      try {
        const details = await userAccountService.getAccountDetails();
        if (!isMinor && (details.showBusinessSection || details.personalAccount?.isBusinessAccount)) {
          setResolvedIsBusinessAccount(true);
        }
        if (details.aliasContext) {
          setAliasDetails(details.aliasContext);
          setAliasDisplayName(details.aliasContext.displayName ?? '');
        }
      } catch (err) {
        console.error('Failed to resolve business account flag from account details:', err);
      }
    };
    loadResolvedBusinessFlag();
  }, [isJointContext, isMinor]);

  const [viewContentTab, setViewContentTab] = useState(0); // 0=Personal, 1=Professional, 2=Social, 3=Interests
  const [selectedAlias, setSelectedAlias] = useState<ProfileAliasDto | null>(null);
  const [profile, setProfile] = useState<ProfileDto | null>(null);
  const [profileAccessCanEdit, setProfileAccessCanEdit] = useState<boolean | null>(null);
  const [aliases, setAliases] = useState<ProfileAliasDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [_isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [manageAliasOpen, setManageAliasOpen] = useState(false);
  
  // Business member aliases — shown in "Your Profiles" tab for business accounts
  const [businessMemberAliases, setBusinessMemberAliases] = useState<JointAccountAlias[]>([]);
  const baseAccountUsername = (user?.username ?? '').trim();
  const baseAccountUsernameLower = baseAccountUsername.toLowerCase();

  /** Primary owner row for base-account login (e.g. CustomerService) when not in tbJointAccountAliases. */
  const ownerTeamMemberEntry = useMemo((): JointAccountAlias | null => {
    if (!resolvedIsBusinessAccount || !user?.userId || !baseAccountUsername) return null;
    const primaryAlias = aliases.find(
      (a) =>
        a.chrAliasName.trim().toLowerCase() === baseAccountUsernameLower ||
        (a.bitIsPrimary && a.chrBaseUserName.trim().toLowerCase() === baseAccountUsernameLower)
    );
    if (!primaryAlias?.idAliasID) return null;
    return {
      aliasId: primaryAlias.idAliasID,
      jointAccountId: jointAccountId ?? '',
      jointAccountName: jointAccountName ?? '',
      linkedUserId: user.userId,
      aliasUsername: primaryAlias.chrAliasName,
      originalInvitedUsername: baseAccountUsername,
      displayName: primaryAlias.chrDisplayName || primaryAlias.chrAliasName,
      isMinor: false,
      canViewBalance: true,
      canViewHistory: true,
      canSpend: true,
      canDeposit: true,
      canWithdraw: true,
      canInviteMembers: true,
      canEditSettings: true,
      currentDailySpent: 0,
      currentWeeklySpent: 0,
      currentMonthlySpent: 0,
      profilePublic: true,
      role: 'PRIMARY',
      status: 1,
      statusText: 'Active',
      created: primaryAlias.dtmCreateDate || new Date().toISOString(),
    };
  }, [
    resolvedIsBusinessAccount,
    user,
    baseAccountUsername,
    baseAccountUsernameLower,
    aliases,
    jointAccountId,
    jointAccountName,
  ]);

  const teamMemberProfiles = useMemo(() => {
    const jointMembers = businessMemberAliases.filter((a) => !a.isMinor);
    const members = [...jointMembers];
    if (ownerTeamMemberEntry) {
      const ownerAlreadyListed = members.some(
        (m) =>
          m.aliasId === ownerTeamMemberEntry.aliasId ||
          m.aliasUsername.trim().toLowerCase() === baseAccountUsernameLower ||
          ((m.role || '').toUpperCase() === 'PRIMARY' &&
            (m.linkedUserId || '').toLowerCase() === (user?.userId ?? '').toLowerCase())
      );
      if (!ownerAlreadyListed) {
        members.unshift(ownerTeamMemberEntry);
      }
    }
    const roleRank = (role?: string) => {
      const r = (role || 'MEMBER').toUpperCase();
      if (r === 'PRIMARY') return 0;
      if (r === 'ADMIN') return 1;
      return 2;
    };
    return members.sort((a, b) => {
      const byRole = roleRank(a.role) - roleRank(b.role);
      if (byRole !== 0) return byRole;
      return (a.displayName || a.aliasUsername).localeCompare(b.displayName || b.aliasUsername);
    });
  }, [businessMemberAliases, ownerTeamMemberEntry, baseAccountUsernameLower, user?.userId]);

  const teamMemberUsernames = useMemo(
    () => new Set(teamMemberProfiles.map((m) => m.aliasUsername.trim().toLowerCase())),
    [teamMemberProfiles]
  );

  /** Premium business identity names not assigned to a team member profile. */
  const businessAliases = useMemo(() => {
    if (!resolvedIsBusinessAccount) return [];
    return [...aliases]
      .filter((a) => a.bitIsActive && !(a.chrAliasName ?? '').includes('*Deleted*'))
      .filter((a) => a.intAliasType === 0)
      .filter((a) => !teamMemberUsernames.has(a.chrAliasName.trim().toLowerCase()))
      .sort((a, b) => {
        if (a.bitIsPrimary && !b.bitIsPrimary) return -1;
        if (!a.bitIsPrimary && b.bitIsPrimary) return 1;
        return (a.chrDisplayName || a.chrAliasName).localeCompare(b.chrDisplayName || b.chrAliasName);
      });
  }, [aliases, resolvedIsBusinessAccount, teamMemberUsernames]);

  const currentUserId = (user?.userId ?? '').toLowerCase();
  const viewerHasBusinessFullAccess = useMemo(() => {
    if (
      resolvedIsBusinessAccount &&
      !isJointContext &&
      ownerTeamMemberEntry &&
      ownerTeamMemberEntry.linkedUserId.toLowerCase() === currentUserId
    ) {
      return true;
    }
    return businessMemberAliases.some((a) => {
      const isCurrentUserAlias = (a.linkedUserId || '').toLowerCase() === currentUserId;
      if (!isCurrentUserAlias) return false;
      return a.canEditSettings || ['PRIMARY', 'ADMIN'].includes((a.role || '').toUpperCase());
    });
  }, [businessMemberAliases, currentUserId, resolvedIsBusinessAccount, isJointContext, ownerTeamMemberEntry]);

  const selectedBusinessMember = useMemo(() => {
    if (!selectedAlias) return undefined;
    const fromJoint = businessMemberAliases.find((a) => a.aliasId === selectedAlias.idAliasID);
    if (fromJoint) return fromJoint;
    const fromTeam = teamMemberProfiles.find((a) => a.aliasId === selectedAlias.idAliasID);
    if (fromTeam) return fromTeam;
    if (
      ownerTeamMemberEntry &&
      selectedAlias.idAliasID === ownerTeamMemberEntry.aliasId
    ) {
      return ownerTeamMemberEntry;
    }
    return undefined;
  }, [businessMemberAliases, teamMemberProfiles, selectedAlias, ownerTeamMemberEntry]);

  const currentViewerBusinessMember = useMemo(() => {
    const fromJoint = businessMemberAliases.find(
      (a) => (a.linkedUserId || '').toLowerCase() === currentUserId
    );
    if (fromJoint) return fromJoint;
    if (
      ownerTeamMemberEntry &&
      ownerTeamMemberEntry.linkedUserId.toLowerCase() === currentUserId
    ) {
      return ownerTeamMemberEntry;
    }
    return undefined;
  }, [businessMemberAliases, currentUserId, ownerTeamMemberEntry]);

  const showProfileBrowser = isMinor || !isJointContext || (resolvedIsBusinessAccount && !isMinor);
  const showFamilyJointPermissions = isJointContext && !resolvedIsBusinessAccount && !isMinor;

  const frontendCanEditSelectedProfile = useMemo(() => {
    if (!selectedAlias) return false;

    if (!resolvedIsBusinessAccount) {
      if (isMinor && aliasId && selectedAlias.idAliasID !== aliasId) return false;
      return true;
    }

    if (selectedBusinessMember) {
      const isSelf = (selectedBusinessMember.linkedUserId || '').toLowerCase() === currentUserId;
      const viewerRole = (currentViewerBusinessMember?.role || 'MEMBER').toUpperCase();
      const targetRole = (selectedBusinessMember.role || 'MEMBER').toUpperCase();
      if (isSelf) return true;
      if (viewerRole === 'MEMBER') return false;
      if (viewerHasBusinessFullAccess || ['PRIMARY', 'ADMIN'].includes(viewerRole)) {
        if (['PRIMARY', 'ADMIN'].includes(targetRole) && !isSelf) return false;
        return true;
      }
      return false;
    }

    return viewerHasBusinessFullAccess;
  }, [
    selectedAlias,
    resolvedIsBusinessAccount,
    isMinor,
    aliasId,
    selectedBusinessMember,
    currentUserId,
    currentViewerBusinessMember,
    viewerHasBusinessFullAccess,
  ]);

  const canEditSelectedProfile = profileAccessCanEdit ?? frontendCanEditSelectedProfile;

  const currentlyViewingText = useMemo(() => {
    if (!selectedAlias) return '';
    const display = (selectedAlias.chrDisplayName || selectedAlias.chrAliasName || '').trim();
    const handle = (selectedAlias.chrAliasName || '').trim();
    const readOnlySuffix = !canEditSelectedProfile ? ' (read-only)' : '';
    if (selectedBusinessMember) {
      const role = (selectedBusinessMember.role || 'MEMBER').toUpperCase();
      return `Currently viewing: ${display} (@${handle}) - Team Member Profile (${role})${readOnlySuffix}`;
    }
    if (resolvedIsBusinessAccount) {
      return `Currently viewing: ${display} (@${handle}) - Business Alias${readOnlySuffix}`;
    }
    return '';
  }, [selectedAlias, selectedBusinessMember, resolvedIsBusinessAccount, canEditSelectedProfile]);

  // Referral state
  const [referralData, setReferralData] = useState<ReferralLinksResponse | null>(null);
  const [referralSchedule, setReferralSchedule] = useState<ReferralSchedule | null>(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedPartnerId, setCopiedPartnerId] = useState<string | null>(null);
  const [expandedPrograms, setExpandedPrograms] = useState<Set<string>>(new Set());
  const [referralsDialogOpen, setReferralsDialogOpen] = useState(false);
  const [referralsDialogTier, setReferralsDialogTier] = useState<number | null>(null);
  const [referralsList, setReferralsList] = useState<ReferredUser[]>([]);
  const [loadingReferrals, setLoadingReferrals] = useState(false);

  useEffect(() => {
    if (!showProfileBrowser) {
      return;
    }
    loadAliases();
    if (!isJointContext) {
      loadReferralData();
    }
    if (resolvedIsBusinessAccount) {
      loadBusinessMemberAliases();
    }
  }, [showProfileBrowser, isJointContext, resolvedIsBusinessAccount, jointAccountId]);
  
  const loadReferralData = async () => {
    try {
      const [links, schedule] = await Promise.all([
        userAccountService.getReferralLinks(),
        userAccountService.getReferralSchedule()
      ]);
      setReferralData(links);
      setReferralSchedule(schedule);
    } catch (err) {
      console.log('Error loading referral data:', err);
    }
  };
  
  const handleCopyReferralCode = async () => {
    if (!referralData?.usernameReferralUrl) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(referralData.usernameReferralUrl);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = referralData.usernameReferralUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (err) {
      window.prompt('Copy this link:', referralData.usernameReferralUrl);
    }
  };

  const handleCopyPartnerCode = async (url: string, codeId: string) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopiedPartnerId(codeId);
      setTimeout(() => setCopiedPartnerId(null), 2000);
    } catch (err) {
      window.prompt('Copy this link:', url);
    }
  };

  const toggleProgramExpand = (codeId: string) => {
    setExpandedPrograms(prev => {
      const next = new Set(prev);
      if (next.has(codeId)) {
        next.delete(codeId);
      } else {
        next.add(codeId);
      }
      return next;
    });
  };
  
  const handleOpenReferralsDialog = async (tier: number | null, partnerCodeId?: string) => {
    setReferralsDialogTier(tier);
    setReferralsDialogOpen(true);
    setLoadingReferrals(true);
    try {
      const list = await userAccountService.getReferrals(tier ?? undefined, partnerCodeId);
      setReferralsList(list);
    } catch (err) {
      console.error('Error loading referrals:', err);
      setReferralsList([]);
    } finally {
      setLoadingReferrals(false);
    }
  };

  useEffect(() => {
    if (selectedAlias) {
      setProfileAccessCanEdit(null);
      loadProfile();
    }
  }, [selectedAlias]);

  const loadAliases = async () => {
    const buildMinorSyntheticAlias = (): ProfileAliasDto => ({
      idAliasID: aliasId!,
      chrBaseUserName: user?.username ?? '',
      chrAliasName: user?.username ?? '',
      chrDisplayName: user?.displayUsername ?? user?.username ?? '',
      intAliasType: 4,
      aliasTypeName: 'Minor',
      bitIsActive: true,
      bitIsPrimary: true,
      bitTrueNameLocked: false,
      bitIdVerified: false,
      dtmCreateDate: new Date().toISOString(),
    });

    try {
      setError(null);
      const data = await socialService.getMyAliases();
      setAliases(data);

      if (isMinor && aliasId) {
        const minorAlias = data.find((a) => a.idAliasID === aliasId);
        setSelectedAlias(minorAlias ?? buildMinorSyntheticAlias());
        return;
      }
      
      if (data.length > 0) {
        const primary = data.find(a => a.bitIsPrimary) || data[0];
        setSelectedAlias((current) => {
          if (current) {
            const stillExists =
              data.some((a) => a.idAliasID === current.idAliasID) ||
              teamMemberProfiles.some((m) => m.aliasId === current.idAliasID);
            if (stillExists) return current;
          }
          if (isJointContext && aliasId) {
            const loginAlias = data.find((a) => a.idAliasID === aliasId);
            if (loginAlias) return loginAlias;
          }
          if (resolvedIsBusinessAccount && !isJointContext && baseAccountUsername) {
            const baseAlias = data.find(
              (a) => a.chrAliasName.trim().toLowerCase() === baseAccountUsernameLower
            );
            if (baseAlias) return baseAlias;
          }
          return primary;
        });
      } else {
        setLoading(false);
        setSelectedAlias(null);
        setError('No profiles found. Create your first profile.');
      }
    } catch (error: any) {
      console.error('Failed to load aliases:', error);
      if (isMinor && aliasId) {
        setSelectedAlias({
          idAliasID: aliasId,
          chrBaseUserName: user?.username ?? '',
          chrAliasName: user?.username ?? '',
          chrDisplayName: user?.displayUsername ?? user?.username ?? '',
          intAliasType: 4,
          aliasTypeName: 'Minor',
          bitIsActive: true,
          bitIsPrimary: true,
          bitTrueNameLocked: false,
          bitIdVerified: false,
          dtmCreateDate: new Date().toISOString(),
        });
        return;
      }
      setLoading(false);
      setError('Unable to connect to Social Profiles API. Please ensure the MailBox.API is running.');
    }
  };

  const loadProfile = async () => {
    if (!selectedAlias) return;
    
    try {
      setLoading(true);
      setError(null);
      const access = await socialService.getProfileByAliasId(selectedAlias.idAliasID);
      setProfileAccessCanEdit(access?.canEdit ?? null);
      if (access?.profile) {
        setProfile({
          ...access.profile,
          interests: access.profile.interests ?? [],
          socialLinks: access.profile.socialLinks ?? [],
        });
      } else {
        // No profile exists yet - create an empty profile for editing
        setProfile({
          idProfileID: '',
          idAliasID: selectedAlias.idAliasID,
          chrAliasName: selectedAlias.chrAliasName,
          chrDisplayName: selectedAlias.chrDisplayName || selectedAlias.chrAliasName,
          txtBio: '',
          chrProfileImageURL: '',
          chrCoverImageURL: '',
          chrLocation: '',
          chrWebsiteURL: '',
          intVisibilityLevel: 1,
          chrJobTitle: '',
          chrCompany: '',
          txtSkills: '',
          chrEducation: '',
          txtProfessionalBio: '',
          socialLinks: [],
          interests: []
        });
        // Keep business/member context in view mode so tabs and alias lists remain visible.
        // Personal accounts can still auto-open editor for first-time profile setup.
        setIsEditing(!resolvedIsBusinessAccount);
      }
    } catch (error: any) {
      console.error('Failed to load profile:', error);
      setError('Failed to load profile data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadBusinessMemberAliases = async () => {
    try {
      if (isJointContext && jointAccountId) {
        const aliases = await jointAccountService.getAliases(jointAccountId);
        setBusinessMemberAliases((aliases || []).filter(a => !a.isMinor));
        return;
      }
      const accounts = await jointAccountService.getMyAccounts();
      const business = accounts?.find(a => a.accountType === 'BUSINESS');
      if (business) {
        const aliases = await jointAccountService.getAliases(business.jointAccountId);
        setBusinessMemberAliases((aliases || []).filter(a => !a.isMinor));
      }
    } catch (err) {
      console.error('Failed to load business member aliases:', err);
    }
  };

  const handleSaveAliasDisplayName = async () => {
    setSavingDisplayName(true);
    try {
      await userAccountService.updateAliasDisplayName(aliasDisplayName);
      setAliasDisplayNameSuccess(true);
      setTimeout(() => setAliasDisplayNameSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to update display name:', err);
    } finally {
      setSavingDisplayName(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSelectAlias = (alias: ProfileAliasDto) => {
    setIsEditing(false);
    setSelectedAlias(alias);
  };

  const handleSelectBusinessMemberProfile = (member: JointAccountAlias) => {
    const matchingAlias = aliases.find(
      (a) => a.chrAliasName.trim().toLowerCase() === member.aliasUsername.trim().toLowerCase()
    );
    if (matchingAlias) {
      handleSelectAlias(matchingAlias);
      return;
    }

    const profileAlias: ProfileAliasDto = {
      idAliasID: member.aliasId,
      chrBaseUserName: member.originalInvitedUsername || member.aliasUsername,
      chrAliasName: member.aliasUsername,
      chrDisplayName: member.displayName || member.aliasUsername,
      intAliasType: 4,
      aliasTypeName: 'Business',
      bitIsActive: true,
      bitIsPrimary: (member.role || '').toUpperCase() === 'PRIMARY',
      bitTrueNameLocked: false,
      bitIdVerified: false,
      dtmCreateDate: member.created,
      dtmLastUsedDate: member.lastLogin,
    };

    setIsEditing(false);
    setSelectedAlias(profileAlias);
  };

  const handleSetPrimaryBusinessAlias = async (alias: ProfileAliasDto) => {
    try {
      await socialService.setPrimaryAlias(alias.idAliasID);
      await handleAliasRefresh();
    } catch (err: any) {
      console.error('Failed to set primary business alias:', err);
      setError(err?.message || 'Failed to set primary business alias.');
    }
  };

  const handleSave = async () => {
    await handleAliasRefresh(); // Refresh aliases and selected profile so Business Aliases reflects latest display names
    if (resolvedIsBusinessAccount) {
      await loadBusinessMemberAliases();
    }
    setIsEditing(false); // Then switch to view (same tab is already in viewContentTab)
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleAliasRefresh = async () => {
    try {
      const data = await socialService.getMyAliases();
      setAliases(data);
      if (data.length > 0) {
        const primary = data.find((a) => a.bitIsPrimary) || data[0];
        let nextSelected = primary;
        setSelectedAlias((current) => {
          if (!current) return primary;
          const stillExists = data.some((a) => a.idAliasID === current.idAliasID);
          nextSelected = stillExists ? data.find((a) => a.idAliasID === current.idAliasID)! : primary;
          return nextSelected;
        });
        const profileAccess = await socialService.getProfileByAliasId(nextSelected.idAliasID);
        setProfileAccessCanEdit(profileAccess?.canEdit ?? null);
        setProfile(profileAccess?.profile
          ? {
              ...profileAccess.profile,
              interests: profileAccess.profile.interests ?? [],
              socialLinks: profileAccess.profile.socialLinks ?? [],
            }
          : null);
      } else {
        setSelectedAlias(null);
        setLoading(false);
        setError('No profiles found. Create your first profile.');
      }
    } catch (err: any) {
      console.error('Failed to refresh aliases:', err);
    }
  };

  const manageAliasDrawer = (
    <Drawer
      anchor="top"
      open={manageAliasOpen}
      onClose={() => setManageAliasOpen(false)}
      sx={{ '& .MuiDrawer-paper': { height: '80vh', maxHeight: '80vh' } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6">Manage Aliases</Typography>
        <IconButton onClick={() => setManageAliasOpen(false)} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ overflow: 'auto', flex: 1, p: 2 }}>
        <AliasManager
          aliases={aliases}
          onRefresh={handleAliasRefresh}
          onClose={() => setManageAliasOpen(false)}
        />
      </Box>
    </Drawer>
  );

  if (isJointContext && !isMinor && !aliasDetails) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography>Loading business member profile...</Typography>
        </Box>
      </Container>
    );
  }

  if (isJointContext && isMinor && loading && !profile && !error && !selectedAlias) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography>Loading your profile...</Typography>
        </Box>
      </Container>
    );
  }

  if (!isJointContext && loading && !profile && !error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography>Loading your profile...</Typography>
        </Box>
      </Container>
    );
  }

  if (!isJointContext && error) {
    return (
      <>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={loadAliases}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
        
        {error.includes('Social Profiles API') && (
          <Paper sx={{ p: 3, bgcolor: 'info.light' }}>
            <Typography variant="h6" gutterBottom>
              ðŸš€ Start the MailBox API
            </Typography>
            <Typography variant="body2" paragraph>
              The Social Profiles feature requires the MailBox.API to be running.
            </Typography>
            <Typography variant="body2" component="div">
              <strong>Option 1: Using Batch File</strong>
              <ol>
                <li>Run <code>START_SERVERS.bat</code> from the project root</li>
                <li>Wait for "Startup Complete!" message</li>
                <li>Click "Retry" above</li>
              </ol>
            </Typography>
            <Typography variant="body2" component="div" sx={{ mt: 2 }}>
              <strong>Option 2: Manual Start</strong>
              <ol>
                <li>Open a terminal</li>
                <li>cd MailBox.API</li>
                <li>dotnet run --urls http://localhost:5220</li>
                <li>Wait for "Now listening on: http://localhost:5220"</li>
                <li>Click "Retry" above</li>
              </ol>
            </Typography>
          </Paper>
        )}
        
        {aliases.length === 0 && !error.includes('Social Profiles API') && (
          <Box sx={{ mt: 3 }}>
            <Button variant="contained" onClick={() => setManageAliasOpen(true)}>
              Manage Aliases
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Create your first profile to get started.
            </Typography>
          </Box>
        )}
      </Container>
      {manageAliasDrawer}
      </>
    );
  }

  if (isMinor && !effectiveMinorAllowedTabs.includes('profile')) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">
          My Profile is not enabled for this account. Ask your parent to allow it in Minor Access settings.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: nativeShell ? { xs: 2, md: 4 } : 4 }}>
      {/* Header with Referral Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4">
            My Profile
          </Typography>
          
          {/* Referral Section - visible from all tabs */}
          {referralData && (
            <Box sx={{ 
              backgroundColor: 'background.paper', 
              border: 1, 
              borderColor: 'divider',
              borderRadius: 2, 
              p: 2,
              maxWidth: 700,
              flex: '1 1 auto'
            }}>
              <Typography variant="subtitle1" fontWeight="bold" color="text.primary" sx={{ mb: 1 }}>
                Refer & Earn
              </Typography>
              {/* Username Referral Code Row */}
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight="bold" color="text.primary">
                  Your Referral Code:
                </Typography>
                <Tooltip title={copiedCode ? 'Copied!' : `Click to copy: ${referralData.usernameReferralUrl}`}>
                  <Chip
                    label={referralData.usernameReferralCode}
                    size="small"
                    color="primary"
                    onClick={handleCopyReferralCode}
                    icon={copiedCode ? <CheckCircleIcon /> : <ContentCopyIcon />}
                    sx={{ cursor: 'pointer' }}
                  />
                </Tooltip>
                
                {/* Tier Referral Links */}
                {referralSchedule && referralSchedule.maxTiers >= 1 && (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => handleOpenReferralsDialog(1)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <PeopleIcon fontSize="small" />
                    Tier 1
                  </Link>
                )}
                {referralSchedule && referralSchedule.maxTiers >= 2 && (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => handleOpenReferralsDialog(2)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <PeopleIcon fontSize="small" />
                    Tier 2
                  </Link>
                )}
                {referralSchedule && referralSchedule.maxTiers >= 3 && (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => handleOpenReferralsDialog(3)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <PeopleIcon fontSize="small" />
                    Tier 3
                  </Link>
                )}
              </Stack>
              
              {/* Inline Referral Schedule Description (for non-partners or when no partner codes) */}
              {referralSchedule && (!referralData.partnerCodes || referralData.partnerCodes.length === 0) && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    <strong>{referralSchedule.scheduleName}</strong>
                    {referralSchedule.description && ` - ${referralSchedule.description}`}
                  </Typography>
                  {referralSchedule.maxTiers > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {referralSchedule.tier1Percentage > 0 && (
                        <>Tier 1: {referralSchedule.tier1Percentage}%{referralSchedule.tier1MaxPayout && ` (max $${referralSchedule.tier1MaxPayout})`}{referralSchedule.tier1DurationMonths && ` for ${referralSchedule.tier1DurationMonths}mo`}</>
                      )}
                      {referralSchedule.maxTiers >= 2 && referralSchedule.tier2Percentage > 0 && (
                        <> | Tier 2: {referralSchedule.tier2Percentage}%{referralSchedule.tier2MaxPayout && ` (max $${referralSchedule.tier2MaxPayout})`}</>
                      )}
                      {referralSchedule.maxTiers >= 3 && referralSchedule.tier3Percentage > 0 && (
                        <> | Tier 3: {referralSchedule.tier3Percentage}%{referralSchedule.tier3MaxPayout && ` (max $${referralSchedule.tier3MaxPayout})`}</>
                      )}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Partner Referral Codes Table - shown when user has multiple referral codes */}
              {referralData.partnerCodes && referralData.partnerCodes.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="body2" fontWeight="bold" color="text.primary" sx={{ mb: 1 }}>
                    Your Referral Programs:
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', py: 0.75, width: 32 }} />
                          <TableCell sx={{ fontWeight: 'bold', py: 0.75 }}>Program</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', py: 0.75 }}>Code</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', py: 0.75 }}>Tiers</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold', py: 0.75 }}>Sign-ups</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {referralData.partnerCodes.map((code) => {
                          const isExpanded = expandedPrograms.has(code.partnerCodeId);
                          return (
                            <React.Fragment key={code.partnerCodeId}>
                              {/* Main program row */}
                              <TableRow 
                                hover 
                                onClick={() => toggleProgramExpand(code.partnerCodeId)}
                                sx={{ cursor: 'pointer', '& > *': { borderBottom: isExpanded ? 'unset' : undefined } }}
                              >
                                <TableCell sx={{ py: 0.5, width: 32 }}>
                                  <IconButton size="small">
                                    {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                  </IconButton>
                                </TableCell>
                                <TableCell sx={{ py: 0.75 }}>
                                  <Typography variant="body2" fontWeight="medium">
                                    {code.displayName || code.scheduleName}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 0.75 }}>
                                  <Tooltip title={copiedPartnerId === code.partnerCodeId ? 'Copied!' : 'Click to copy referral link'}>
                                    <Chip
                                      label={code.referralCode}
                                      size="small"
                                      color={copiedPartnerId === code.partnerCodeId ? 'success' : 'secondary'}
                                      variant="outlined"
                                      onClick={(e) => { e.stopPropagation(); handleCopyPartnerCode(code.referralUrl, code.partnerCodeId); }}
                                      icon={copiedPartnerId === code.partnerCodeId ? <CheckCircleIcon /> : <ContentCopyIcon />}
                                      sx={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.75rem' }}
                                    />
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="center" sx={{ py: 0.75 }}>
                                  <Chip label={`${code.maxTiers}-tier`} size="small" variant="outlined" color="info" />
                                </TableCell>
                                <TableCell align="center" sx={{ py: 0.75 }}>
                                  <Chip
                                    label={
                                      code.maxTiers === 1
                                        ? String(code.tier1Count)
                                        : code.maxTiers === 2
                                        ? `${code.tier1Count} / ${code.tier2Count}`
                                        : `${code.tier1Count} / ${code.tier2Count} / ${code.tier3Count}`
                                    }
                                    size="small"
                                    color={(code.tier1Count + code.tier2Count + code.tier3Count) > 0 ? 'success' : 'default'}
                                    variant="outlined"
                                    title={`Tier 1: ${code.tier1Count} direct sign-ups via this code`}
                                  />
                                </TableCell>
                              </TableRow>

                              {/* Expandable tier details row */}
                              <TableRow>
                                <TableCell sx={{ py: 0, px: 0 }} colSpan={5}>
                                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50' }}>
                                      {/* Description */}
                                      {code.scheduleDescription && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                          {code.scheduleDescription}
                                        </Typography>
                                      )}

                                      {/* Share Links */}
                                      <Box sx={{ mb: 2 }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight="medium" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                          Share Links
                                        </Typography>
                                        <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                                          {/* Identified link — shows the partner's username in the URL */}
                                          <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography variant="caption" color="text.secondary" sx={{ width: 80, flexShrink: 0 }}>
                                              Identified
                                            </Typography>
                                            <Chip
                                              label={code.referralUrl}
                                              size="small"
                                              variant="outlined"
                                              color={copiedPartnerId === code.partnerCodeId ? 'success' : 'default'}
                                              icon={copiedPartnerId === code.partnerCodeId ? <CheckCircleIcon /> : <ContentCopyIcon />}
                                              onClick={(e) => { e.stopPropagation(); handleCopyPartnerCode(code.referralUrl, code.partnerCodeId); }}
                                              sx={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.7rem', maxWidth: '100%' }}
                                            />
                                          </Stack>
                                          {/* Anonymous link — short opaque code, partner identity hidden */}
                                          <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography variant="caption" color="text.secondary" sx={{ width: 80, flexShrink: 0 }}>
                                              Anonymous
                                            </Typography>
                                            <Chip
                                              label={code.shortReferralUrl}
                                              size="small"
                                              variant="outlined"
                                              color={copiedPartnerId === `${code.partnerCodeId}-anon` ? 'success' : 'default'}
                                              icon={copiedPartnerId === `${code.partnerCodeId}-anon` ? <CheckCircleIcon /> : <ContentCopyIcon />}
                                              onClick={(e) => { e.stopPropagation(); handleCopyPartnerCode(code.shortReferralUrl, `${code.partnerCodeId}-anon`); }}
                                              sx={{ cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.7rem', maxWidth: '100%' }}
                                            />
                                          </Stack>
                                        </Stack>
                                      </Box>

                                      {/* Tier details grid */}
                                      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                                        {code.tier1Percentage > 0 && (
                                          <Paper variant="outlined" sx={{ p: 1.5, minWidth: 160, flex: '1 1 auto' }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                              <Typography variant="subtitle2" color="primary.main">
                                                Tier 1 (Direct)
                                              </Typography>
                                              <Link
                                                component="button"
                                                variant="caption"
                                                onClick={() => handleOpenReferralsDialog(1, code.partnerCodeId)}
                                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                              >
                                                <PeopleIcon sx={{ fontSize: 14 }} />
                                                View ({code.tier1Count})
                                              </Link>
                                            </Stack>
                                            <Typography variant="body2">
                                              <strong>{code.tier1Percentage}%</strong> of ad revenue
                                            </Typography>
                                            {code.tier1MaxPayout != null && (
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                Max payout: ${code.tier1MaxPayout}
                                              </Typography>
                                            )}
                                            {code.tier1DurationMonths != null && (
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                Duration: {code.tier1DurationMonths} months
                                              </Typography>
                                            )}
                                          </Paper>
                                        )}
                                        
                                        {code.maxTiers >= 2 && code.tier2Percentage > 0 && (
                                          <Paper variant="outlined" sx={{ p: 1.5, minWidth: 160, flex: '1 1 auto' }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                              <Typography variant="subtitle2" color="primary.main">
                                                Tier 2 (Indirect)
                                              </Typography>
                                              <Link
                                                component="button"
                                                variant="caption"
                                                onClick={() => handleOpenReferralsDialog(2, code.partnerCodeId)}
                                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                              >
                                                <PeopleIcon sx={{ fontSize: 14 }} />
                                                View ({code.tier2Count})
                                              </Link>
                                            </Stack>
                                            <Typography variant="body2">
                                              <strong>{code.tier2Percentage}%</strong> of ad revenue
                                            </Typography>
                                            {code.tier2MaxPayout != null && (
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                Max payout: ${code.tier2MaxPayout}
                                              </Typography>
                                            )}
                                            {code.tier2DurationMonths != null && (
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                Duration: {code.tier2DurationMonths} months
                                              </Typography>
                                            )}
                                          </Paper>
                                        )}
                                        
                                        {code.maxTiers >= 3 && code.tier3Percentage > 0 && (
                                          <Paper variant="outlined" sx={{ p: 1.5, minWidth: 160, flex: '1 1 auto' }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                              <Typography variant="subtitle2" color="primary.main">
                                                Tier 3 (2nd Indirect)
                                              </Typography>
                                              <Link
                                                component="button"
                                                variant="caption"
                                                onClick={() => handleOpenReferralsDialog(3, code.partnerCodeId)}
                                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                              >
                                                <PeopleIcon sx={{ fontSize: 14 }} />
                                                View ({code.tier3Count})
                                              </Link>
                                            </Stack>
                                            <Typography variant="body2">
                                              <strong>{code.tier3Percentage}%</strong> of ad revenue
                                            </Typography>
                                            {code.tier3MaxPayout != null && (
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                Max payout: ${code.tier3MaxPayout}
                                              </Typography>
                                            )}
                                            {code.tier3DurationMonths != null && (
                                              <Typography variant="caption" color="text.secondary" display="block">
                                                Duration: {code.tier3DurationMonths} months
                                              </Typography>
                                            )}
                                          </Paper>
                                        )}
                                      </Stack>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {visibleTabIndices.length > 0 && (
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
        <ResponsiveScrollTabs
          tabs={profileTabs}
          value={String(currentTab)}
          onChange={(value) => {
            setCurrentTab(Number(value));
            setIsEditing(false);
          }}
          selectLabel="Sections"
          ariaLabel="Profile sections"
        />
      </Box>
      )}

      {visibleTabIndices.map((realIndex, displayIndex) => (
        <TabPanel key={realIndex} value={currentTab} index={displayIndex}>
          {realIndex === 0 && showFamilyJointPermissions && aliasDetails && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <BusinessIcon color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    {aliasDetails.aliasUsername}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Member of <strong>{aliasDetails.jointAccountName}</strong> &bull; {aliasDetails.role}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Display Name</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Box
                    component="input"
                    value={aliasDisplayName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAliasDisplayName(e.target.value)}
                    placeholder="Enter your display name"
                    sx={{
                      flex: 1, p: 1, border: '1px solid', borderColor: 'divider',
                      borderRadius: 1, fontSize: '0.875rem', fontFamily: 'inherit',
                      '&:focus': { outline: 'none', borderColor: 'primary.main' }
                    }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveAliasDisplayName}
                    disabled={savingDisplayName}
                  >
                    {savingDisplayName ? 'Saving…' : 'Save'}
                  </Button>
                </Box>
                {aliasDisplayNameSuccess && (
                  <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                    Display name updated.
                  </Typography>
                )}
              </Box>

              <Typography variant="subtitle2" gutterBottom>Permissions</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {aliasDetails.canViewBalance && <Chip size="small" label="View Balance" color="info" variant="outlined" />}
                {aliasDetails.canViewHistory && <Chip size="small" label="View History" color="info" variant="outlined" />}
                {aliasDetails.canSpend && <Chip size="small" label="Send Messages" color="success" variant="outlined" />}
                {aliasDetails.canDeposit && <Chip size="small" label={storeMode ? 'Deposit Permission' : 'Add Funds'} color="success" variant="outlined" />}
                {aliasDetails.canWithdraw && <Chip size="small" label="Withdraw" color="success" variant="outlined" />}
                {aliasDetails.canInviteMembers && <Chip size="small" label="Invite Members" color="warning" variant="outlined" />}
                {aliasDetails.canEditSettings && <Chip size="small" label="Edit Settings" color="warning" variant="outlined" />}
              </Box>

              {aliasDetails.allowedTabs.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Accessible Tabs</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {aliasDetails.allowedTabs.map(tab => (
                      <Chip key={tab} size="small" label={tab} variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                Messages you send and receive use the shared <strong>{aliasDetails.jointAccountName}</strong> account balance.
                Use the <strong>Account Settings</strong> tab for personal information and identity verification.
              </Typography>
            </Paper>
          )}
          {realIndex === 0 && showProfileBrowser && selectedAlias && (
            <>
              {!_isEditing ? (
                <>
                {!isMinor && (
                <ProfileAliasNavigator
                  mode={resolvedIsBusinessAccount ? 'business' : 'personal'}
                  selectedAliasId={selectedAlias.idAliasID}
                  personalAliases={aliases}
                  teamMembers={teamMemberProfiles}
                  businessAliases={businessAliases}
                  onSelectPersonalAlias={handleSelectAlias}
                  onSelectTeamMember={handleSelectBusinessMemberProfile}
                  onSelectBusinessAlias={handleSelectAlias}
                  onManageAliases={resolvedIsBusinessAccount ? undefined : () => setManageAliasOpen(true)}
                  viewerHasBusinessFullAccess={viewerHasBusinessFullAccess}
                  onSetPrimaryBusinessAlias={handleSetPrimaryBusinessAlias}
                />
                )}
                {currentlyViewingText && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {currentlyViewingText}
                  </Alert>
                )}
                {profile ? (
                <ProfileView
                  profile={profile}
                  isOwner={canEditSelectedProfile}
                  onEdit={canEditSelectedProfile ? handleEdit : undefined}
                  contentTabValue={viewContentTab}
                  onContentTabChange={setViewContentTab}
                  selectedAliasType={selectedAlias.intAliasType}
                />
                ) : loading ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : null}
                </>
              
              ) : (
                canEditSelectedProfile ? (
                  profile ? (
                  <EnhancedProfileEditor
                    profile={profile}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    onProfileRefresh={loadProfile}
                    initialTab={viewContentTab}
                    onTabChange={setViewContentTab}
                  />
                  ) : null
                ) : (
                  <Alert severity="warning">You can view this profile, but you do not have permission to edit it.</Alert>
                )
              )}
            </>
          )}
          {realIndex === 1 && (
            <AccountSettings onChangePasswordClick={() => setChangePasswordOpen(true)} />
          )}
          {realIndex === 2 && (resolvedIsBusinessAccount ? <AdditionalMembersManager /> : <MinorAccessManager />)}
        </TabPanel>
      ))}

      {visibleTabIndices.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          No sections are currently visible for this page.
        </Typography>
      )}

      {manageAliasDrawer}

      {/* Change Password Dialog */}
      <ChangePassword
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
      
      {/* Referrals List Dialog */}
      <Dialog 
        open={referralsDialogOpen} 
        onClose={() => setReferralsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {referralsDialogTier 
            ? `Tier ${referralsDialogTier} Referrals` 
            : 'All Referrals'}
        </DialogTitle>
        <DialogContent>
          {loadingReferrals ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : referralsList.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No referrals found{referralsDialogTier ? ` for Tier ${referralsDialogTier}` : ''}.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Username</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Joined</TableCell>
                    {!referralsDialogTier && <TableCell>Tier</TableCell>}
                    <TableCell align="right">Earnings</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {referralsList.map((referral) => (
                    <TableRow key={referral.referredUserID}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {referral.referredUsername}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {referral.referredFullName || '-'}
                      </TableCell>
                      <TableCell>
                        {fmtDate(referral.accountCreatedDate)}
                      </TableCell>
                      {!referralsDialogTier && (
                        <TableCell>
                          <Chip 
                            label={`Tier ${referral.tierLevel}`} 
                            size="small" 
                            color={referral.tierLevel === 1 ? 'primary' : referral.tierLevel === 2 ? 'secondary' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                      )}
                      <TableCell align="right">
                        ${referral.cumulativePayout.toFixed(2)}
                        {referral.maxPayoutReached && (
                          <Chip label="Max" size="small" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReferralsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyProfile;


